import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import CustomButton from "../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
// TODO: crear loadPackagesResource en backend_helper cuando esté el endpoint
import { loadPackagesResource } from "../../helpers/configRoutes/backend_helper";
import CustomInput from "../../components/form/CustomInput";
import { showFormErrors } from "../../helpers/showFormErrors";
import { currencyUSD } from "../../helpers/forms/formater";

const AddUpdLoadPackage = ({ modal, setModal, packageItem, setFilters, filters }) => {

    const [loading, setLoading] = useState(false);

    const baseitem = {
        id: null,
        name: '',
        price: '',
        taxes: 0,
        loads: '',
        validity_days: '',
        active: false,
    };

    const validation = useFormik({
        initialValues: packageItem || baseitem,
        validationSchema: Yup.object({
            name: Yup.string().required('Name is required'),
            price: Yup.number()
                .typeError('Price must be a number')
                .min(0, 'Price must be positive')
                .required('Price is required'),
            taxes: Yup.number()
                .typeError('Taxes must be a number')
                .min(0, 'Taxes must be positive')
                .required('Taxes are required'),
            loads: Yup.number()
                .typeError('Loads must be a number')
                .integer('Loads must be an integer')
                .min(1, 'At least 1 load')
                .required('Loads count is required'),
            validity_days: Yup.number()
                .typeError('Validity must be a number')
                .integer('Validity must be an integer')
                .min(1, 'At least 1 day')
                .required('Validity is required'),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await loadPackagesResource(
                values.id ? 'put' : 'post',
                values,
                values.id
            );
            if (response.status) {
                setLoading(false);
                setFilters({ ...filters, search: filters.search });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors);
            }
            setLoading(false);
        }
    });

    useEffect(() => {
        if (packageItem?.id) {
            /* merge con baseitem para que al editar paquetes viejos no queden campos undefined */
            validation.setValues({ ...baseitem, ...packageItem });
        } else {
            validation.resetForm({ values: baseitem });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal]);

    /* ---------- helpers live ---------- */
    const v = validation.values;
    const pricePerLoad =
        parseFloat(v.price) > 0 && parseInt(v.loads) > 0
            ? parseFloat(v.price) / parseInt(v.loads)
            : 0;
    const taxesAmount =
        parseFloat(v.price) > 0 && parseFloat(v.taxes) > 0
            ? (parseFloat(v.price) * parseFloat(v.taxes)) / 100
            : 0;

    return (
        <BaseModalAnimated modal={modal} setModal={setModal} maxWidth="max-w-2xl">
            <form
                onSubmit={e => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                }}
                className="flex flex-col"
            >
                {/* HEADER */}
                <div className="border-b border-cs-border pb-2 mb-2">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-cs-primary-soft text-cs-primary-dark">
                            <i className="fas fa-truck-loading" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-cs-dark">
                                {validation.values?.id ? 'Edit Load Package' : 'Create Load Package'}
                            </h2>
                            <p className="text-sm text-cs-muted">
                                Define how many loads are included and their validity
                            </p>
                        </div>
                    </div>
                </div>

                {/* CONTENT */}
                <div className="space-y-8">

                    {/* PACKAGE INFO */}
                    <section>
                        <h3 className="text-sm font-semibold text-cs-dark mb-2 uppercase tracking-wide">
                            Package information
                        </h3>

                        <CustomInput
                            label="Package Name"
                            name="name"
                            error={validation.errors.name}
                            value={validation.values.name}
                            handleChange={validation.handleChange}
                        />
                    </section>

                    {/* PRICING */}
                    <section>
                        <h3 className="text-sm font-semibold text-cs-dark  uppercase tracking-wide">
                            Pricing
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CustomInput
                                label="Price (USD)"
                                type="number"
                                name="price"
                                error={validation.errors.price}
                                value={validation.values.price}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Taxes %"
                                type="number"
                                name="taxes"
                                error={validation.errors.taxes}
                                value={validation.values.taxes}
                                handleChange={validation.handleChange}
                            />
                        </div>

                        {taxesAmount > 0 && (
                            <p className="text-xs text-cs-muted mt-1">
                                Taxes amount:{' '}
                                <span className="font-medium text-cs-dark">
                                    {currencyUSD(taxesAmount)}
                                </span>
                            </p>
                        )}
                    </section>

                    {/* PACKAGE CONTENTS */}
                    <section>
                        <h3 className="text-sm font-semibold text-cs-dark mb-1 uppercase tracking-wide">
                            Package contents
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CustomInput
                                label="Loads included"
                                type="number"
                                name="loads"
                                error={validation.errors.loads}
                                value={validation.values.loads}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Validity (days)"
                                type="number"
                                name="validity_days"
                                error={validation.errors.validity_days}
                                value={validation.values.validity_days}
                                handleChange={validation.handleChange}
                            />
                        </div>

                        {pricePerLoad > 0 && (
                            <div className="mt-3 rounded-lg bg-cs-primary-soft border border-cs-primary/20 p-3 flex items-center gap-2">
                                <i className="fas fa-calculator text-cs-primary-dark text-xs" />
                                <p className="text-xs text-cs-primary-dark">
                                    Price per load:{' '}
                                    <span className="font-semibold">
                                        {currencyUSD(pricePerLoad)}
                                    </span>
                                </p>
                            </div>
                        )}
                    </section>
                </div>

                {/* FOOTER */}
                <div className="mt-1 pt-3 border-t border-cs-border flex justify-end gap-4">
                    <CustomButton
                        type="button"
                        onClick={() => setModal({ ...modal, show: false })}
                        className="bg-cs-bg-alt text-cs-dark"
                    >
                        Cancel
                    </CustomButton>

                    <CustomButton
                        loading={loading}
                        textLoad="Saving"
                        type="success"
                        className="px-10"
                    >
                        {validation.values?.id ? 'Save changes' : 'Create package'}
                    </CustomButton>
                </div>
            </form>
        </BaseModalAnimated>
    );
};

export default AddUpdLoadPackage;